// API client for Paranormal Archetypes web frontend.
// Mirrors the Expo `services/api.ts` contract; only the storage swap changes
// (localStorage vs. AsyncStorage).

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export function getToken() {
  return typeof window !== "undefined" ? localStorage.getItem("session_token") : null;
}
export function setToken(t) {
  if (typeof window !== "undefined") localStorage.setItem("session_token", t);
}
export function clearToken() {
  if (typeof window !== "undefined") localStorage.removeItem("session_token");
}

export async function apiCall(endpoint, { method = "GET", body, headers } = {}) {
  const token = getToken();
  const h = { "Content-Type": "application/json", ...(headers || {}) };
  if (token) h["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/api${endpoint}`, {
    method,
    headers: h,
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  });

  if (res.status === 401) {
    clearToken();
    const err = await res.json().catch(() => ({ detail: "Your session has expired. Please sign in again." }));
    // Route to login on 401 (unless we're already there)
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login") && !window.location.pathname.startsWith("/callback")) {
      window.location.replace("/login");
    }
    throw new Error(err.detail || "Session expired");
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Request failed" }));
    const detail = typeof err.detail === "string" ? err.detail : (err.detail?.message || "Request failed");
    const e = new Error(detail);
    e.status = res.status;
    e.detail = err.detail;
    throw e;
  }

  return res.json();
}

// Endpoint groups
export const authApi = {
  exchangeSession: (session_id) => apiCall("/auth/session", { method: "POST", body: { session_id } }),
  getMe: () => apiCall("/auth/me"),
  logout: () => apiCall("/auth/logout", { method: "POST" }),
  devLogin: (email, name) => apiCall("/dev/test-login", { method: "POST", body: { email, name } }),
  magicLinkRequest: (email) => apiCall("/auth/email/request", { method: "POST", body: { email } }),
  magicLinkVerify: (token) => apiCall("/auth/email/verify", { method: "POST", body: { token } }),
};

export const userApi = {
  updateUser: (data) => apiCall("/users/me", { method: "PUT", body: data }),
  verifyCode: (access_code) => apiCall("/users/verify-code", { method: "POST", body: { access_code } }),
  restorePurchases: () => apiCall("/users/restore-purchases", { method: "POST" }),
  deleteAccount: () => apiCall("/users/me", { method: "DELETE" }),
};

export const archetypeApi = {
  getAll: () => apiCall("/archetypes"),
  getList: () => apiCall("/archetypes/list"),
  getOne: (name) => apiCall(`/archetypes/${encodeURIComponent(name)}`),
};

export const quizApi = {
  getQuestions: () => apiCall("/quiz/questions"),
  calculate: (answers) => apiCall("/quiz/calculate", { method: "POST", body: { answers } }),
  submit: (answers) => apiCall("/quiz/submit", { method: "POST", body: { answers } }),
  latestResult: () => apiCall("/quiz/results/latest"),
  getMyResults: () => apiCall("/quiz/my-results"),
};

export const productsApi = {
  getCatalog: () => apiCall("/products"),
  getMyUnlocks: () => apiCall("/me/unlocks"),
};

export const paymentApi = {
  createCheckout: (product_id, origin) => apiCall("/checkout/create", { method: "POST", body: { product_id, origin } }),
  getCheckoutStatus: (order_id) => apiCall(`/checkout/${encodeURIComponent(order_id)}`),
  mockComplete: (order_id, outcome) => apiCall(`/checkout/${encodeURIComponent(order_id)}/mock-complete`, { method: "POST", body: { outcome } }),
  listOrders: () => apiCall("/orders"),
};

export const libraryApi = {
  getLibrary: () => apiCall("/library"),
  getItem: (itemId) => apiCall(`/library/${encodeURIComponent(itemId)}`),
  fileUrl: (itemId, token, download = false) =>
    `${API_BASE}/api/library/file/${encodeURIComponent(itemId)}?token=${encodeURIComponent(token)}${download ? "&download=1" : ""}`,
};

export const growthApi = {
  getFieldCompanion: () => apiCall("/field-companion"),
  getGrowthPath: (archetype) => apiCall(`/growth-path${archetype ? `?archetype=${encodeURIComponent(archetype)}` : ""}`),
  getDynamics: () => apiCall("/dynamics"),
  getFieldGuidance: () => apiCall("/field-guidance"),
  addReflection: (text, prompt) => apiCall("/field-companion/reflections", { method: "POST", body: { text, prompt } }),
  listReflections: () => apiCall("/field-companion/reflections"),
};

export const manualApi = {
  getManual: () => apiCall("/manual"),
  addFieldNote: (template_key, data, title, investigation) =>
    apiCall("/manual/field-notes", { method: "POST", body: { template_key, data, title, investigation } }),
  updateFieldNote: (noteId, template_key, data, title, investigation) =>
    apiCall(`/manual/field-notes/${encodeURIComponent(noteId)}`, { method: "PUT", body: { template_key, data, title, investigation } }),
  listFieldNotes: (template_key) =>
    apiCall(`/manual/field-notes${template_key ? `?template_key=${encodeURIComponent(template_key)}` : ""}`),
  deleteFieldNote: (noteId) => apiCall(`/manual/field-notes/${encodeURIComponent(noteId)}`, { method: "DELETE" }),
  getHistory: (q) => apiCall(`/manual/history${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  getActive: () => apiCall("/manual/active"),
};

export const referenceApi = {
  getOnboarding: () => apiCall("/onboarding"),
  getInvestigationTypes: () => apiCall("/reference/investigation-types"),
};

export const adminApi = {
  listCustomers: (q) => apiCall(`/admin/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  getCustomer: (userId) => apiCall(`/admin/customers/${encodeURIComponent(userId)}`),
  generateCode: (purchaser_email, product_id) => apiCall("/admin/generate-code", { method: "POST", body: { purchaser_email: purchaser_email || null, product_id: product_id || "quiz" } }),
  voidCode: (code) => apiCall(`/admin/access-codes/${encodeURIComponent(code)}/void`, { method: "POST" }),
  resetCode: (code) => apiCall(`/admin/access-codes/${encodeURIComponent(code)}/reset`, { method: "POST" }),
  reassign: (code, to_email) => apiCall("/admin/reassign", { method: "POST", body: { code, to_email } }),
  setPurchaserEmail: (code, to_email) => apiCall(`/admin/access-codes/${encodeURIComponent(code)}/set-purchaser-email`, { method: "POST", body: { to_email } }),
  listCodes: () => apiCall("/admin/access-codes"),
  grantProduct: (user_id, product_id) => apiCall("/admin/customers/grant", { method: "POST", body: { user_id, product_id } }),
  revokeProduct: (user_id, product_id) => apiCall("/admin/customers/revoke", { method: "POST", body: { user_id, product_id } }),
  grandfatherList: () => apiCall("/admin/grandfather"),
  grandfatherImport: (entries) => apiCall("/admin/grandfather/import", { method: "POST", body: { entries } }),
  listProducts: () => apiCall("/admin/products"),
  updateProduct: (product_id, data) => apiCall(`/admin/products/${encodeURIComponent(product_id)}`, { method: "PUT", body: data }),
  participants: () => apiCall("/admin/participants"),
  orderLookup: (q) => apiCall(`/admin/order-lookup?q=${encodeURIComponent(q)}`),
};

export { API_BASE };
